import React, { useContext, useState } from 'react';
import { useIntl } from '@edx/frontend-platform/i18n';

import { contactApi } from '../../api';
import { AppDataContext } from '../../context';
import { useHeaderFooter } from '../../hooks';
import { SpinLoader } from '../../components';
import { EmailIcon, LinkedinIcon, YoutubeIcon } from '../../img/icons/contact';
import { windowScrollTo } from '../../utils';

import './index.scss';
import messages from './messages';

import heroImg from '../../img/contact/CONTACTUS-LWC.jpg';
import twitterImg from '../../img/contact/twitter-blue.png';

interface ContactProps {
  children?: React.ReactNode;
}

interface FormData {
  fullName: string;
  email: string;
  phoneNumber: string;
  company: string;
  message: string;
}

const DEFAULT_FORM_DATA = {
  fullName: '',
  email: '',
  phoneNumber: '',
  company: '',
  message: '',
};

const Contact: React.FC<ContactProps> = ({ children }) => {
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA);
  const [errors, setErrors] = useState<FormData>(DEFAULT_FORM_DATA);
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);

  const intl = useIntl();
  const { authenticatedUser } = useContext(AppDataContext);
  const { headerContent, footerContent } = useHeaderFooter(children);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
    setIsFormSubmitted(false);
  };

  const validateForm = () => {
    const newErrors = { ...DEFAULT_FORM_DATA };
    let isValid = true;

    if (!formData.fullName.trim()) {
      newErrors.fullName = intl.formatMessage(messages.requiredField);
      isValid = false;
    }
    if (!formData.message.trim()) {
      newErrors.message = intl.formatMessage(messages.requiredField);
      isValid = false;
    }
    if (!formData.email.trim()) {
      newErrors.email = intl.formatMessage(messages.requiredField);
      isValid = false;
    } else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(formData.email)) {
        newErrors.email = intl.formatMessage(messages.invalidEmail);
        isValid = false;
      }
    }
    if (formData.phoneNumber.trim()) {
      const phonePattern = /^\+?[0-9\s()-]{7,20}$/;
      if (!phonePattern.test(formData.phoneNumber)) {
        newErrors.phoneNumber = intl.formatMessage(messages.invalidPhoneNumber);
        isValid = false;
      }
    }
    setErrors(newErrors);
    return isValid;
  };

  const scrollToForm = () => {
    const formElement = document.querySelector<HTMLElement>('.contact-form');

    if (formElement) {
      windowScrollTo({
        top: formElement.offsetTop - 100,
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      scrollToForm();
      return;
    }

    setIsFormSubmitting(true);

    await contactApi.submitContactForm(Boolean(authenticatedUser), formData, () => {
      setFormData(DEFAULT_FORM_DATA);
      setIsFormSubmitted(true);
    });

    setIsFormSubmitting(false);
  };

  return (
    <>
      <input type="hidden" id="anPageName" name="page" defaultValue="about" />
      <div className="container-center-horizontal">
        <div className="about screen contact">
          <div className="homepage ">
            {headerContent}
            <div className="overlap-group6">
              <div className="hero-image">
                <div className="overlap-group-1">
                  <img
                    className="overlap-group-item"
                    src={heroImg}
                    alt=""
                  />
                  <h1 className="about-us recklessneue-regular-normal-white-100-1px">
                    <span className="recklessneue-regular-normal-white-100-1px">
                      {intl.formatMessage(messages.contact)}
                    </span>
                    <span className="instrumentserif-normal-white-100-1px">
                      {' '}&amp; {intl.formatMessage(messages.support)}
                    </span>
                  </h1>
                </div>
              </div>
            </div>
          </div>
          <div className="course-overview-text">
            <div className="overlap-group5">
              <p className="lorem-ipsum-dolor-si-1 neuehaasgroteskdisplaypro-45-light-congress-blue-24px">
                {intl.formatMessage(messages.welcomeText)}
              </p>
            </div>
          </div>
          <div className="legatum-container the-course contact-row">
            <div className="c-section c-title">
              <div className="leg-title">
                {intl.formatMessage(messages.contactUsPrompt)}
              </div>
              <div className="contact-footer">
                <div className="hr" />
                <p className="f-first-p">{intl.formatMessage(messages.sendEmail)}</p>
                <p className="f-email">
                  <a href="mailto:support@leadingwithcharacter.com">
                    support@leadingwithcharacter.com
                  </a>
                </p>
                <div className="f-media">
                  <a href="mailto:support@leadingwithcharacter.com">
                    <EmailIcon />
                  </a>
                  <a
                    href="https://x.com/LWCcourse"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img height="25px" src={twitterImg} alt="" />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/leadingwithcharacter/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <LinkedinIcon />
                  </a>
                  <a href="https://www.youtube.com/@LeadingWithCharacter" target="_blank" rel="noreferrer">
                    <YoutubeIcon />
                  </a>
                </div>
              </div>
            </div>
            <div className="contact-form">
              <div className="form-row">
                <label htmlFor="fullName">
                  {intl.formatMessage(messages.fullNameLabel)}*
                </label>
                <input
                  name="fullName"
                  id="fullName"
                  type="text"
                  placeholder={intl.formatMessage(messages.fullNamePlaceholder)}
                  required
                  value={formData.fullName}
                  onChange={handleInputChange}
                />
                <span className="form-error" />
                {errors.fullName && (
                  <span className="form-error form-error-custom">{errors.fullName}</span>
                )}
              </div>
              <div className="form-row">
                <label htmlFor="email">{intl.formatMessage(messages.emailLabel)}*</label>
                <input
                  name="email"
                  id="email"
                  type="email"
                  placeholder={intl.formatMessage(messages.emailPlaceholder)}
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                />
                {errors.email && <span className="form-error form-error-custom">{errors.email}</span>}
              </div>
              <div className="form-row">
                <label htmlFor="phoneNumber">{intl.formatMessage(messages.phoneNumberLabel)}</label>
                <input
                  name="phoneNumber"
                  id="phoneNumber"
                  type="tel"
                  placeholder={intl.formatMessage(messages.phoneNumberPlaceholder)}
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                />
                {errors.phoneNumber && (
                  <span className="form-error form-error-custom">{errors.phoneNumber}</span>
                )}
              </div>
              <div className="form-row">
                <label htmlFor="company">{intl.formatMessage(messages.companyLabel)}</label>
                <input
                  name="company"
                  id="company"
                  type="text"
                  placeholder={intl.formatMessage(messages.companyPlaceholder)}
                  value={formData.company}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-row">
                <label htmlFor="message">{intl.formatMessage(messages.howCanWeHelpLabel)}*</label>
                <textarea
                  name="message"
                  id="message"
                  cols={30}
                  rows={10}
                  required
                  placeholder={intl.formatMessage(messages.howCanWeHelpLabelPlaceholder)}
                  value={formData.message}
                  onChange={handleInputChange}
                />
                {errors.message && (
                  <span className="form-error form-error-custom">{errors.message}</span>
                )}
              </div>
              <div className="hr" />
              <div className="form-button-row">
                <button
                  type="button"
                  className="contact-btn"
                  onClick={!isFormSubmitting ? handleSubmit : undefined}
                  disabled={isFormSubmitting}
                >
                  {intl.formatMessage(messages.sendButton)}
                  {isFormSubmitting && (
                    <span style={{ marginLeft: '20px' }}>
                      <SpinLoader size={20} color="#fff" />
                    </span>
                  )}
                </button>
              </div>
              {isFormSubmitted && (
                <div className="success-message">
                  <span>
                    {intl.formatMessage(messages.thankYouMessage)}
                  </span>
                </div>
              )}
            </div>
          </div>
          {footerContent}
        </div>
      </div>
    </>
  );
};

export default Contact;
